import type { ApiError } from './types';

export type AuthStrategy =
  | { kind: 'bearer'; token: string }
  | { kind: 'kiosk'; token: string }
  | { kind: 'player-token'; token: string }
  | { kind: 'dev'; sub: string; email: string }
  | null;

export interface AuthCtx {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  path: string;
}

export interface ClientOptions {
  baseUrl: string;
  /**
   * Called per-request so the auth context (path + method) can pick
   * the right strategy. E.g. `/sessions/{joined-sid}/...` uses the
   * player token, but `/events/...` uses the host's cognito identity
   * even when a player token happens to be in the store.
   */
  getAuth?: (ctx: AuthCtx) => Promise<AuthStrategy> | AuthStrategy;
  fetchImpl?: typeof fetch;
}

export class ApiCallError extends Error {
  constructor(
    public status: number,
    public payload: ApiError | undefined,
    message: string,
  ) {
    super(message);
    this.name = 'ApiCallError';
  }
}

function buildUrl(base: string, path: string, query?: Record<string, unknown>) {
  const url = new URL(path.replace(/^\//, ''), base.endsWith('/') ? base : `${base}/`);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null) continue;
      url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

export interface ApiClient {
  request<T = unknown>(opts: {
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    path: string;
    query?: Record<string, unknown>;
    body?: unknown;
    auth?: AuthStrategy;
  }): Promise<T>;
}

export function createApiClient(options: ClientOptions): ApiClient {
  const fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);

  return {
    async request({ method, path, query, body, auth }) {
      const headers = new Headers({ 'Content-Type': 'application/json' });

      const resolvedAuth = auth ?? (await options.getAuth?.({ method, path })) ?? null;
      if (resolvedAuth?.kind === 'bearer') {
        headers.set('Authorization', `Bearer ${resolvedAuth.token}`);
      } else if (resolvedAuth?.kind === 'kiosk') {
        headers.set('X-Kiosk-Token', resolvedAuth.token);
      } else if (resolvedAuth?.kind === 'player-token') {
        headers.set('X-Player-Token', resolvedAuth.token);
      } else if (resolvedAuth?.kind === 'dev') {
        headers.set('x-user-sub', resolvedAuth.sub);
        headers.set('x-user-email', resolvedAuth.email);
      }

      const init: RequestInit = {
        method,
        headers,
      };
      if (body !== undefined) init.body = JSON.stringify(body);

      const res = await fetchImpl(buildUrl(options.baseUrl, path, query), init);

      if (res.status === 204) return undefined as never;

      const text = await res.text();
      // parse-safe: HTTP response body boundary — generated openapi-fetch types narrow at call sites
      const payload: unknown = text ? JSON.parse(text) : undefined;

      if (!res.ok) {
        const err = (payload as ApiError | undefined) ?? {
          code: 'INTERNAL_ERROR',
          message: `HTTP ${res.status}`,
        };
        throw new ApiCallError(res.status, err, err.message);
      }
      return payload as never;
    },
  };
}
